import React, { useState, useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";
import CheckboxTree from "react-checkbox-tree";
import "react-checkbox-tree/lib/react-checkbox-tree.css";

function AssignAssessmentPlan() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId = localStorage.getItem("centerId");

  const {
    register,
    control,
    handleSubmit,
    setValue,
    trigger,
    reset,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      centerId: null,
      assessmentPlanId: null,
      accountString: "",
    },
  });

  const [assessmentPlans, setAssessmentPlans] = useState([]);
  const [allCentersData, setAllCentersData] = useState([]);
  const [centersData, setCentersData] = useState([]); // Tree data
  const [checked, setChecked] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [loadingCenters, setLoadingCenters] = useState(false);

  // Watch form values
  const selectedCenter = useWatch({ control, name: "centerId" });
  const selectedAssessmentPlan = useWatch({
    control,
    name: "assessmentPlanId",
  });

  // Fetch Assessment Plans
  useEffect(() => {
    const fetchAssessmentPlans = async () => {
      if (!organizationId || !token) return;
      try {
        const response = await get(
          `AssessmentPlan/GetAllAssessmentPlanByOrganizationIdAsync?organizationId=${organizationId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setAssessmentPlans(response || []);
      } catch (error) {
        console.error("Error fetching assessment plans:", error);
        toast.error("Failed to load assessment plans");
      }
    };
    fetchAssessmentPlans();
  }, [organizationId, token]);

  // Fetch All Centers (for dropdown)
  useEffect(() => {
    const fetchCenters = async () => {
      if (!organizationId || !token) return;
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const centers = response || [];
        setAllCentersData(centers);

        // Auto-select saved center if exists
        if (
          savedCenterId &&
          savedCenterId !== "null" &&
          savedCenterId !== "0"
        ) {
          const saved = centers.find((c) => c.id === parseInt(savedCenterId));
          if (saved) {
            setValue("centerId", { value: saved.id, label: saved.name });
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };
    fetchCenters();
  }, [organizationId, token, savedCenterId, setValue]);

  // Fetch Centers + Classes based on selected Assessment Plan & Center
  const fetchCentersWithClasses = async () => {
    if (!selectedAssessmentPlan?.value || !token) {
      setCentersData([]);
      setChecked([]);
      setExpanded([]);
      return;
    }

    setLoadingCenters(true);
    try {
      const centerIdToUse = selectedCenter?.value || savedCenterId || 0;

      const response = await get(
        `AssessmentPlan/GetCenterByOrganizationAndLevelsId?organizationId=${organizationId}&centerId=${centerIdToUse}&assessmentPlanId=${selectedAssessmentPlan.value}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const centers = response.center || [];

      // Extract pre-checked class nodes
      const preChecked = [];
      centers.forEach((center) => {
        center.classes.forEach((cls) => {
          if (cls.status === true) {
            preChecked.push(`center-${center.centerId}-class-${cls.id}`);
          }
        });
      });

      setCentersData(centers);
      setChecked(preChecked);

      // Optional: auto-expand all centers
      const expandedNodes = centers.map((c) => `center-${c.centerId}`);
      setExpanded(expandedNodes);
    } catch (error) {
      console.error("Error fetching centers with classes:", error);
      toast.error("Failed to load classes");
      setCentersData([]);
      setChecked([]);
      setExpanded([]);
    } finally {
      setLoadingCenters(false);
    }
  };
  useEffect(() => {
    fetchCentersWithClasses();
  }, [
    selectedAssessmentPlan,
    selectedCenter,
    organizationId,
    token,
    savedCenterId,
  ]);

  // Transform data for CheckboxTree
  const transformTreeData = (data) => {
    return data.map((center) => ({
      value: `center-${center.centerId}`,
      label: center.centerName,
      children: center.classes.map((cls) => ({
        value: `center-${center.centerId}-class-${cls.id}`,
        label: cls.name,
        classId: cls.id,
        centerId: center.centerId,
      })),
    }));
  };

  // Transform checked values to payload
  const transformToPayload = (checkedItems) => {
    return checkedItems
      .filter((item) => item.includes("-class-")) // Only class nodes
      .map((value) => {
        const parts = value.split("-");
        const centerId = parseInt(parts[1], 10);
        const classIdIndex = parts.indexOf("class") + 1;
        const centerClassId = parseInt(parts[classIdIndex], 10);

        return {
          createdBy: localStorage.getItem("userId"),
          centerId,
          centerClassId,
          assessmentPlanId: selectedAssessmentPlan.value,
        };
      });
  };

  // Submit handler
  const saveData = async () => {
    // if (checked.length === 0) {
    //   toast.error("Please select at least one class");
    //   return;
    // }

    const payload = transformToPayload(checked);

    try {
      const response = await post(
        `AssessmentPlan/AssignAssessmentPlanCenterClassesAsync?centerId=${selectedCenter.value}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.isSuccess) {
        toast.success("Success!");
        setChecked([]);
        setExpanded([]);
      } else {
        toast.error(response.message || "Failed to assign");
      }
      fetchCentersWithClasses();
    } catch (error) {
      console.error("Error saving assignment:", error);
      toast.error("An error occurred while saving");
    }
  };

  const treeNodes = transformTreeData(centersData);

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Assign Assessment Plan</h2>
      </div>

      <form className="new-form-style" onSubmit={handleSubmit(saveData)}>
        <div className="row g-3">
          <div className="col-md-5">
            <div className="card">
              <div className="card-body">
                <div className="row g-3">
                  {/* Center Dropdown */}
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Center <span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="centerId"
                      control={control}
                      rules={{ required: "Center is required" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isClearable={
                            !savedCenterId ||
                            savedCenterId === "null" ||
                            savedCenterId === "0"
                          }
                          isDisabled={
                            savedCenterId &&
                            savedCenterId !== "null" &&
                            savedCenterId !== "0"
                          }
                          options={allCentersData.map((c) => ({
                            value: c.id,
                            label: c.name,
                          }))}
                          placeholder="Select Center"
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                    {errors.centerId && (
                      <small className="text-danger">
                        {errors.centerId.message}
                      </small>
                    )}
                  </div>

                  {/* Assessment Plan Dropdown */}
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Assessment Plan{" "}
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="assessmentPlanId"
                      control={control}
                      rules={{ required: "Assessment Plan is required" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={assessmentPlans.map((ap) => ({
                            value: ap.id,
                            label: ap.name,
                          }))}
                          placeholder="Select Assessment Plan"
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isClearable
                        />
                      )}
                    />
                    {errors.assessmentPlanId && (
                      <small className="text-danger">
                        {errors.assessmentPlanId.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tree View */}
          <div className="col-md-7">
            <div className="card">
              <div className="card-body">
                {loadingCenters ? (
                  <p>Loading classes...</p>
                ) : centersData.length > 0 ? (
                  <>
                    <h6>Select Classes to Assign</h6>
                    <div
                      className="custom-checkbox-tree border rounded p-3"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <CheckboxTree
                        nodes={treeNodes}
                        checked={checked}
                        expanded={expanded}
                        onCheck={setChecked}
                        onExpand={setExpanded}
                        iconsClass="fa5"
                        showNodeIcon={false}
                      />
                    </div>

                    <div className="mt-3">
                      <button
                        type="submit"
                        className="btn btn-primary btn-sm"
                        disabled={isSubmitting 
                          // || checked.length === 0
                        }
                      >
                        {isSubmitting ? "Saving..." : "Assign Assessment Plan"}
                      </button>
                    </div>
                  </>
                ) : selectedAssessmentPlan ? (
                  <p className="text-muted">
                    No classes found for selected center and plan.
                  </p>
                ) : (
                  <p className="text-muted">
                    Please select an Assessment Plan to view classes.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default AssignAssessmentPlan;
